import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { vestigasApi } from '~/services/kyClient';

import {
  type InvoiceFileExportCreated,
  type InvoiceFileExportRequest,
} from './types';
import {
  invoiceFileExportCreatedSchema,
  invoiceFileExportRequestSchema,
} from './validations';

/**
 * Create and enqueue a ZIP export containing files attached to invoices.
 *
 * @param {InvoiceFileExportRequest} payload - Invoice IDs to include
 * @returns {Promise<InvoiceFileExportCreated>} The queued job and request IDs
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/create_invoice_file_export_invoices_file_exports_post
 */
export const createInvoiceFileExport = async (
  payload: InvoiceFileExportRequest,
): Promise<InvoiceFileExportCreated> => {
  const response = await vestigasApi
    .post(ENDPOINT.DATA_EXPORT.CREATE_INVOICE_FILES(), {
      json: payload,
      validate: {
        body: invoiceFileExportRequestSchema,
        response: invoiceFileExportCreatedSchema,
      },
    })
    .json<InvoiceFileExportCreated>();

  return response;
};

/**
 * React Query mutation hook for creating an invoice file export job.
 */
export const useMutationCreateInvoiceFileExport = (
  options?: Omit<
    UseMutationOptions<
      InvoiceFileExportCreated,
      Error,
      InvoiceFileExportRequest
    >,
    'mutationFn'
  >,
) => {
  return useMutation({
    ...options,
    mutationFn: createInvoiceFileExport,
  });
};
